/**
 * ✨ SpotTile component file: renders the main Spot tile UI for cash trading,
 * including header, rates, main fields, commercial margin, and footer.
 * Handles dynamic resizing logic based on tile state.
 */

import { isDefined, isNonEmpty } from '@sgme/fp';
import { Spacer, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useEffect } from 'react';
import { shallowEqual } from 'react-redux';

import { CashTileHeader } from '@/App/tiles/CashTile/shared/CashTileHeader.tsx';
import { CashTileMainFields } from '@/App/tiles/CashTile/shared/CashTileMainFields.tsx';
import { CreateOrder } from '@/App/tiles/CashTile/shared/CreateOrderFromCash';
import { SpotCommercialMargin } from '@/App/tiles/CashTile/SpotTile/SpotCommercialMargin.tsx';
import { SpotFooter } from '@/App/tiles/CashTile/SpotTile/SpotFooter.tsx';
import { SpotRates } from '@/App/tiles/CashTile/SpotTile/SpotRates.tsx';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { resetCash } from '@/store/cash/thunks/resetCash.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

/**
 * 💡 Props for the SpotTile component, controlling its state and behavior.
 */
export type SpotTileProps = {
  tileId: string; // 💡 Unique identifier for the tile instance
  isCommercialMarginVisible: boolean; // 💡 Controls visibility of the commercial margin section
  onCommercialMarginVisibilityToggle: () => void; // 💡 Handler to toggle commercial margin visibility
};

/**
 * ⚙️ SpotTile main React component: displays the spot trading tile UI and manages its layout and state.
 *
 * @example
 * <SpotTile tileId="TILE1" isCommercialMarginVisible={true} onCommercialMarginVisibilityToggle={fn} />
 *
 * 🚩🚩🚩 @returns JSX.Element - the rendered SpotTile component
 */
export const SpotTile = (props: SpotTileProps) => {
  // 💡 Destructure props for easier access
  const { tileId, isCommercialMarginVisible, onCommercialMarginVisibilityToggle } = props;
  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();

  /**
   * 💡 Selects tile state from the Redux store, including currency pair, RFS requirement, and client override.
   */
  const { currencyPair, tileError } = useAppSelector((state) => {
    // 💡 Retrieve the tile object from the store
    const tile = selectCashTile(state, tileId);
    // 💡 Extract currency pair from tile properties
    const currencyPair = tile?.properties.currencyPair;

    // 🚩 Return the relevant state for the component
    return {
      currencyPair,
      tileError: tile?.tradeCapture?.status === 'ERROR' ? tile.tradeCapture.error : undefined,
    };
  }, shallowEqual);

  const onResetTile = () => {
    // 🚩 Reset the tile state, typically called when an error occurs
    // This function should be implemented to reset the tile's state in the store
    sgwtWebAnalytics?.trackEvent('Cash', 'Tile reseted', tileId);

    dispatch(resetCash(tileId));
  };

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  if (isDefined(tileError)) {
    return <ErrorTile tileId={tileId} onReset={onResetTile} />;
  }

  // 🚩 Render (renders the SpotTile layout with header, rates, fields, margin, and footer)
  return (
    <AutoAdvanceForm className="d-flex w-100 h-100">
      <VStack className="py-2" fullWidth fullHeight>
        {/* 💡 Spot tile header with controls and info */}
        <CashTileHeader tileId={tileId} />

        {/* 💡 Only render main content if a currency pair is selected */}
        {isNonEmpty(currencyPair) && (
          <>
            {/* 💡 Spot rates display */}
            <SpotRates tileId={tileId} />

            {/* 💡 Main input fields for the spot tile */}
            <CashTileMainFields tileId={tileId} />

            {/* 💡 Commercial margin section, visibility controlled by props */}
            <SpotCommercialMargin
              tileId={tileId}
              isCommercialMarginVisible={isCommercialMarginVisible}
              onCommercialMarginVisibilityToggle={onCommercialMarginVisibilityToggle}
            />

            {/* 💡 Spacer for layout separation */}
            <Spacer />

            {/* 💡 Footer with additional actions/info */}
            <SpotFooter tileId={tileId} />

            <CreateOrder tileId={tileId} />
          </>
        )}
      </VStack>
    </AutoAdvanceForm>
  );
};
